package com.speedsoftware.nibbler.ui.projects

import androidx.compose.ui.draw.clip
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.speedsoftware.nibbler.models.CreateProjectRequest
import com.speedsoftware.nibbler.models.ProjectDto
import com.speedsoftware.nibbler.ui.AppState
import com.speedsoftware.nibbler.ui.Screen
import com.speedsoftware.nibbler.ui.components.PropertyIcon
import com.speedsoftware.nibbler.ui.components.PropertyIconView
import com.speedsoftware.nibbler.ui.components.ProjectImage
import com.speedsoftware.nibbler.ui.components.ResponsiveScaffold
import com.speedsoftware.nibbler.ui.theme.NibblerColors
import kotlinx.coroutines.launch

@Composable
fun ProjectsScreen(appState: AppState) {
    var projects by remember { mutableStateOf<List<ProjectDto>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var showCreateDialog by remember { mutableStateOf(false) }

    var newProjectKey by remember { mutableStateOf("") }
    var newProjectName by remember { mutableStateOf("") }
    var newProjectDescription by remember { mutableStateOf("") }
    var dialogError by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    fun loadProjects() {
        isLoading = true
        scope.launch {
            appState.apiClient.getProjects().onSuccess { projects = it }
            isLoading = false
        }
    }

    LaunchedEffect(Unit) { loadProjects() }

    ResponsiveScaffold(
        appState = appState,
        title = "Projects",
        onNewIssueClick = { showCreateDialog = true },
        actions = {
            Row(
                modifier = Modifier
                    .height(38.dp)
                    .background(NibblerColors.Accent, RoundedCornerShape(10.dp))
                    .clickable { showCreateDialog = true }
                    .padding(horizontal = 14.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp)
            ) {
                PropertyIconView(PropertyIcon.PLUS, size = 14.dp, color = Color.White)
                Text("New project", fontSize = 13.sp, fontWeight = FontWeight.SemiBold, color = Color.White)
            }
        }
    ) {
        if (isLoading && projects.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = NibblerColors.Accent)
            }
        } else {
            BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
                val columns = when {
                    maxWidth >= 1100.dp -> 3
                    maxWidth >= 680.dp -> 2
                    else -> 1
                }
                val firstName = appState.currentUser?.name?.substringBefore(" ").orEmpty()
                Column(
                    modifier = Modifier.fillMaxSize().verticalScroll(rememberScrollState()).padding(horizontal = 24.dp, vertical = 24.dp),
                    verticalArrangement = Arrangement.spacedBy(20.dp)
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        Text(
                            if (firstName.isBlank()) "Welcome back" else "Welcome back, $firstName",
                            fontSize = 20.sp,
                            fontWeight = FontWeight.SemiBold,
                            color = NibblerColors.OnSurface
                        )
                        Text(
                            if (projects.isEmpty()) "Create your first project to get started."
                            else "${projects.size} ${if (projects.size == 1) "project" else "projects"} you are part of.",
                            fontSize = 13.sp,
                            color = NibblerColors.OnSurfaceVariant
                        )
                    }

                    if (projects.isEmpty()) {
                        EmptyProjects(onCreate = { showCreateDialog = true })
                    } else {
                        projects.chunked(columns).forEach { rowProjects ->
                            Row(horizontalArrangement = Arrangement.spacedBy(16.dp), modifier = Modifier.fillMaxWidth()) {
                                rowProjects.forEach { project ->
                                    ProjectCard(
                                        project = project,
                                        onClick = { appState.navigateTo(Screen.ProjectDetail(project)) },
                                        modifier = Modifier.weight(1f)
                                    )
                                }
                                // Keep the last row's cards the same width as the others
                                repeat(columns - rowProjects.size) { Spacer(modifier = Modifier.weight(1f)) }
                            }
                        }
                    }
                }
            }
        }

        if (showCreateDialog) {
            AlertDialog(
                onDismissRequest = { showCreateDialog = false },
                containerColor = NibblerColors.SurfaceContainerHigh,
                shape = RoundedCornerShape(16.dp),
                title = { Text("New project", fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        OutlinedTextField(
                            value = newProjectKey,
                            onValueChange = { newProjectKey = it.uppercase().take(8) },
                            label = { Text("Key (e.g. NIB)") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                        OutlinedTextField(
                            value = newProjectName,
                            onValueChange = { newProjectName = it },
                            label = { Text("Name") },
                            singleLine = true,
                            modifier = Modifier.fillMaxWidth()
                        )
                        OutlinedTextField(
                            value = newProjectDescription,
                            onValueChange = { newProjectDescription = it },
                            label = { Text("Description") },
                            modifier = Modifier.fillMaxWidth()
                        )
                        dialogError?.let { Text(it, color = NibblerColors.Error, fontSize = 12.sp) }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (newProjectKey.isBlank() || newProjectName.isBlank()) {
                                dialogError = "Key and name are required"
                                return@Button
                            }
                            scope.launch {
                                appState.apiClient.createProject(
                                    CreateProjectRequest(key = newProjectKey, name = newProjectName, description = newProjectDescription)
                                ).onSuccess { created ->
                                    showCreateDialog = false
                                    newProjectKey = ""
                                    newProjectName = ""
                                    newProjectDescription = ""
                                    dialogError = null
                                    loadProjects()
                                    appState.navigateTo(Screen.ProjectDetail(created))
                                }.onFailure { dialogError = it.message ?: "Could not create the project" }
                            }
                        },
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Accent, contentColor = Color.White)
                    ) { Text("Create") }
                },
                dismissButton = {
                    TextButton(onClick = { showCreateDialog = false }) { Text("Cancel", color = NibblerColors.OnSurfaceVariant) }
                }
            )
        }
    }
}

@Composable
private fun EmptyProjects(onCreate: () -> Unit) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(16.dp))
            .padding(40.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Text("No projects yet", fontSize = 16.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface)
        Text("A project holds your boards, issues and documentation.", fontSize = 13.sp, color = NibblerColors.OnSurfaceVariant)
        Spacer(modifier = Modifier.height(6.dp))
        Button(
            onClick = onCreate,
            shape = RoundedCornerShape(10.dp),
            colors = ButtonDefaults.buttonColors(containerColor = NibblerColors.Accent, contentColor = Color.White)
        ) { Text("Create a project") }
    }
}

/** A project as a card: picture, name and key, description, and a real progress bar from the issue counts. */
@Composable
fun ProjectCard(project: ProjectDto, onClick: () -> Unit, modifier: Modifier = Modifier) {
    val progress = if (project.issueCount > 0) project.doneCount.toFloat() / project.issueCount else 0f
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(14.dp))
            .clickable { onClick() }
            .background(NibblerColors.SurfaceContainer, RoundedCornerShape(14.dp))
            .border(1.dp, NibblerColors.OutlineVariant, RoundedCornerShape(14.dp))
            .padding(18.dp),
        verticalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            ProjectImage(project = project, size = 42.dp)
            Column(modifier = Modifier.weight(1f)) {
                Text(project.name, fontSize = 16.sp, fontWeight = FontWeight.SemiBold, color = NibblerColors.OnSurface, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(project.key, fontSize = 12.sp, color = NibblerColors.OnSurfaceVariant)
            }
        }
        Text(
            project.description.ifBlank { "No description" },
            fontSize = 13.sp,
            color = NibblerColors.OnSurfaceVariant,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis,
            modifier = Modifier.height(36.dp)
        )
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Box(modifier = Modifier.fillMaxWidth().height(6.dp).background(NibblerColors.SurfaceContainerHighest, RoundedCornerShape(3.dp))) {
                if (progress > 0f) {
                    Box(modifier = Modifier.fillMaxWidth(progress).height(6.dp).background(NibblerColors.Success, RoundedCornerShape(3.dp)))
                }
            }
            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                Text(
                    if (project.issueCount == 0) "No issues yet" else "${project.doneCount} of ${project.issueCount} done",
                    fontSize = 12.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
                Text(
                    "${project.memberCount} ${if (project.memberCount == 1) "member" else "members"}",
                    fontSize = 12.sp,
                    color = NibblerColors.OnSurfaceVariant
                )
            }
        }
    }
}

@androidx.compose.ui.tooling.preview.Preview
@Composable
private fun ProjectCardPreview() {
    Box(modifier = Modifier.background(NibblerColors.Background).padding(16.dp)) {
        ProjectCard(
            project = ProjectDto(
                id = "p1", key = "NIB", name = "Nibbler App", description = "Cross-platform issue tracker",
                issueCount = 12, doneCount = 5, memberCount = 3, createdAt = ""
            ),
            onClick = {}
        )
    }
}
